import * as React from 'react';
import closeReadingPane from 'mini-mail-reading-pane-view/lib/utils/closeReadingPane';
import FolderTreeType from 'mini-mail-folder-store/lib/store/schema/FolderTreeType';
import getChevronIcon from '../utils/getChevronIcon';
import getChevronIconAriaLabel from '../utils/getChevronIconAriaLabel';
import getDefaultIconForFolder from '../utils/getDefaultIconForFolder';
import getFolderItemAriaLabel from '../utils/getFolderItemAriaLabel';
import getMoveToFolderNodeViewState from 'mini-mail-folder-store/lib/selectors/getMoveToFolderNodeViewState';
import getSelectedTableViewId from 'mini-mail-list-store/lib/utils/getSelectedTableViewId';
import getTreeNodeViewId from '../utils/getTreeNodeViewId';
import getValidFolders from '../utils/getValidFolders';
import isItemView from 'owa-mail-store/lib/utils/isItemView';
import mailStore from 'owa-mail-store/lib/store/Store';
import toggleMoveToFolderNodeViewState from 'mini-mail-folder-store/lib/actions/toggleMoveToFolderNodeViewState';
import toggleMoveToFolderPane from 'mini-mail-folder-store/lib/actions/toggleMoveToFolderPane';
import { getIconClassName } from '@uifabric/styling/lib/index';
import { getRTL } from '@uifabric/utilities';
import { lazyMoveConversation, lazyMoveItem } from 'mini-mail-triage-actions';
import { logUsage } from 'owa-analytics';
import { MiniIcons } from 'owa-mini-icons';
import { moveSelectedRows } from '../utils/moveSelectedRows';
import { selectlistviewTriageItem } from 'mini-mail-list-actions';
import { Tree, TreeNodeData } from 'mini-tree';

const styles = require('./FolderPaneTree.scss');
const classNames = require('classnames/bind');

export interface MoveToFolderPaneTreeProps {
    folderIds: string[];
    rootDisplayName: string;
    folderTreeType: FolderTreeType;
    onFolderSelected?: () => void;
    isRootExpanded?: boolean;
    itemRefs?: React.RefObject<HTMLElement>[];
    startIndex?: number;
    totalLength?: number;
}

/**
 * Wrapper component to render moveToFolderTree nodes
 * @param props MoveToFolderPaneTreeProps
 */
class MoveToFolderPaneTree extends React.PureComponent<MoveToFolderPaneTreeProps, {}> {
    private getChevronProps = (
        folderId: string,
        childFolderIds: string[],
        isExpanded: boolean,
        isFavoriteTree: boolean
    ) => {
        const isRtl = getRTL();
        if (!isFavoriteTree && childFolderIds.length) {
            return {
                iconName: getChevronIcon(isExpanded, isRtl),
                onClick: (depth?: number) => {
                    toggleMoveToFolderNodeViewState(folderId),
                        logUsage('MoveToFolderPane', [
                            depth,
                            getMoveToFolderNodeViewState(folderId).isExpanded,
                        ]);
                },
                getChevronIconAriaLabel,
            };
        }

        return {
            iconName: getDefaultIconForFolder(folderId, isFavoriteTree),
        };
    };

    private onFolderSelected = async (folderId: string): Promise<void> => {
        const selectedFolderId = mailStore.folders.selectedFolderId;
        if (selectedFolderId !== folderId) {
            const { triageActionSelectedId, selectedId } = mailStore.mailModule ?? {};
            const idToMove = triageActionSelectedId || selectedId;
            if (triageActionSelectedId) {
                selectlistviewTriageItem(null);
            }
            // If the move to folder pane is shown in reading pane, use moveConversation instead of moveSelectedConversations
            if (idToMove) {
                if (isItemView()) {
                    await lazyMoveItem(idToMove, folderId, getSelectedTableViewId());
                } else {
                    await lazyMoveConversation(idToMove, folderId, getSelectedTableViewId());
                }
            } else {
                moveSelectedRows(folderId);
            }
            toggleMoveToFolderPane();
            closeReadingPane();
            this.props.onFolderSelected?.();
        }
    };

    private renderSelectedIcon = (): JSX.Element => (
        <i
            className={classNames(
                'ms-Icon',
                'isSelected',
                'mailListItem-selectBox',
                getIconClassName(MiniIcons.CompletedSolid),
                styles.selectedIcon
            )}
            aria-hidden={'true'}
        />
    );

    private getNodeData = (folderId: string): TreeNodeData => {
        const folder = mailStore.folders.folderTable.get(folderId);
        const folderViewState = getMoveToFolderNodeViewState(folderId);
        const isSelected = folderId === mailStore.folders.selectedFolderId;
        const filteredChildFolders = getValidFolders(folder.childFolderIds);
        const isFavoriteTree = this.props.folderTreeType === FolderTreeType.Favorites;
        const treeNodeViewId = getTreeNodeViewId(this.props.rootDisplayName, folderId);

        return {
            displayName: folder.DisplayName,
            iconProps: this.getChevronProps(
                folderId,
                filteredChildFolders,
                folderViewState.isExpanded,
                isFavoriteTree
            ),
            isExpanded: folderViewState.isExpanded,
            rightCharm: isSelected ? this.renderSelectedIcon() : null,
            onClick: this.onFolderSelected,
            childNodeIds: !isFavoriteTree ? filteredChildFolders : [],
            ariaLabelForNode: getFolderItemAriaLabel(
                folder.DisplayName,
                null /** count */,
                true,
                isSelected,
                null /** isTotalCount */
            ),
            treeNodeViewId: treeNodeViewId,
        };
    };

    render() {
        const { folderIds, rootDisplayName, startIndex, itemRefs, totalLength } = this.props;

        return (
            <div className={styles.treeContainer}>
                <Tree
                    rootDisplayName={rootDisplayName}
                    isRootExpanded={true}
                    nodeIds={folderIds}
                    getNodeData={this.getNodeData}
                    startIndex={startIndex}
                    itemRefs={itemRefs}
                    totalLength={totalLength}
                />
            </div>
        );
    }
}

export default MoveToFolderPaneTree;
